import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * P1-B — Out-of-reports modal (the component name keeps "Credit": it is an
 * identifier, not copy).
 *
 * Shown inline (no navigation) when the user attempts to analyze with
 * no analysis reports left. Provides three exits: upgrade plan, contact, close.
 *
 * The `requiredCu` row was dropped in CU-Based-pricing-3000-char-8-13.md
 * (T3.11): an analysis uses exactly 1 report, so "Required CU" was always
 * going to read 1 and told the user nothing the remaining row did not.
 * `shortfall` remains optional — a caller with a real 402 body can pass it —
 * and is worded in reports, not CU.
 *
 * THE WORDING (KG이니시스 review, 2026-10-02 — .claude/TODO/
 * Portone-KGinisys-Pricing-fix-10-2.md T2.3). KG classes "크레딧" as 환금성
 * (cash-equivalent), and nothing can be topped up: the old "크레딧을 충전" /
 * "top up credits" pointed at a purchase that does not exist. The modal now
 * says the PROVIDED reports are used up and points at the plan. `monthly`
 * (the server's `tier_grants_monthly`) is what lets it say "이번 달": a trial
 * org's 5 reports are one-time. It defaults to false — the claim that cannot
 * be wrong.
 */
export default function CreditExhaustedModal({ open, onClose, tierName, balance = 0, shortfall, monthly = false }) {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  if (!open) return null;

  const planLabel = tierName || (isKo ? '알 수 없음' : 'Unknown');
  // 건 only on a real number. App.js hands this modal the shared DASH when the
  // balance could not be read (O3), and "—건" would dress an unknown as a count.
  const countUnit = isKo && typeof balance === 'number' ? '건' : '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cem-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          background: '#FFFFFF',
          borderRadius: 8,
          padding: '32px 28px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          color: '#1A0A1E',
        }}
      >
        <h2 id="cem-title" style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>
          {isKo
            ? (monthly ? '이번 달 제공된 분석 리포트를 모두 이용하셨습니다' : '제공된 분석 리포트를 모두 이용하셨습니다')
            : (monthly ? "You've used all of this month's analysis reports" : "You've used all of your analysis reports")}
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: '#444444', lineHeight: 1.6 }}>
          {isKo
            ? '분석을 계속하시려면 플랜을 업그레이드하거나 문의해 주세요.'
            : 'To keep analyzing, upgrade your plan or contact us.'}
        </p>

        <div
          style={{
            border: '1px solid #E5E5E5',
            borderRadius: 6,
            padding: '12px 16px',
            background: '#FAFAFA',
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 6 }}>
            <span style={{ color: '#888888' }}>{isKo ? '현재 플랜' : 'Current plan'}</span>
            <span style={{ color: '#1A0A1E', fontWeight: 600 }}>{planLabel}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
            <span style={{ color: '#888888' }}>{isKo ? '남은 분석 리포트' : 'Reports left'}</span>
            <span style={{ color: '#B91C1C', fontWeight: 700 }}>
              {balance}{countUnit}
            </span>
          </div>
          {shortfall != null && shortfall > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginTop: 6 }}>
              <span style={{ color: '#888888' }}>{isKo ? '추가로 필요한 리포트' : 'Reports needed'}</span>
              <span style={{ color: '#B91C1C', fontWeight: 700 }}>
                {isKo ? `${shortfall}건` : `${shortfall}`}
              </span>
            </div>
          )}
        </div>

        <Link
          to="/profile/manage-membership"
          onClick={onClose}
          style={{ textDecoration: 'none', display: 'block' }}
        >
          <button
            type="button"
            style={{
              width: '100%',
              padding: 14,
              background: 'linear-gradient(135deg, #931DA4 0%, #123293 100%)',
              color: '#FFFFFF',
              fontSize: 15,
              fontWeight: 600,
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {isKo ? '플랜 업그레이드하기' : 'Upgrade plan'}
          </button>
        </Link>

        <a
          href="mailto:hello@whereat.kr"
          style={{
            display: 'block',
            textAlign: 'center',
            marginTop: 12,
            padding: 10,
            color: '#931DA4',
            textDecoration: 'none',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {isKo ? '문의하기' : 'Contact us'}
        </a>

        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%',
            background: 'none',
            border: 'none',
            color: '#888888',
            fontSize: 13,
            marginTop: 6,
            cursor: 'pointer',
            padding: 8,
            fontFamily: 'inherit',
          }}
        >
          {isKo ? '닫기' : 'Close'}
        </button>
      </div>
    </div>
  );
}
